import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import './Contact.css';

function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    serviceDate: '',
    message: '',
    mathAnswer: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [mathProblem, setMathProblem] = useState({
    num1: 0,
    num2: 0,
    answer: 0
  });
  // GA4 tracking states
  const [formStarted, setFormStarted] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  
  // Get today's date for the min date
  const today = new Date().toISOString().split('T')[0];
  // Get date 1 year from now for the max date
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 1);
  const maxDateString = maxDate.toISOString().split('T')[0];

  useEffect(() => {
    // Start the timer when the component mounts
    setStartTime(Date.now());
    
    // Generate math problem only once when component mounts
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    const answer = num1 + num2;
    
    console.log('New math problem generated:', { num1, num2, answer });
    
    setMathProblem({
      num1,
      num2,
      answer
    });
  }, []);

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[\d\s-]{10,}$/;

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!formData.serviceDate) {
      newErrors.serviceDate = 'Please select a preferred date';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    // Check if enough time has passed (at least 10 seconds)
    const timeElapsed = (Date.now() - startTime) / 1000;
    if (timeElapsed < 10) {
      newErrors.timing = `Please take at least 10 seconds to fill out the form (${Math.ceil(10 - timeElapsed)} seconds remaining)`;
    }

    // Validate math answer
    const userAnswer = parseInt(formData.mathAnswer);
    console.log('Validating math answer:', { 
      userAnswer, 
      correctAnswer: mathProblem.answer,
      isCorrect: userAnswer === mathProblem.answer 
    });

    if (userAnswer !== mathProblem.answer) {
      newErrors.mathAnswer = 'Please check your answer';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Mark form as started for abandonment tracking
    if (!formStarted) setFormStarted(true);
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form-name': 'contact',
          ...formData
        }).toString()
      });

      if (response.ok) {
        // GA4: Track form submission
        window.gtag && window.gtag('event', 'contact_form_submit', {
          event_category: 'engagement',
          event_label: 'Contact Page',
          value: 1
        });
        setFormSubmitted(true);
        setSubmitMessage('Thanks for getting in touch! I’ll respond within 24 hours.');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          serviceDate: '',
          message: '',
          mathAnswer: ''
        });
        // Reset the timer
        setStartTime(Date.now());
        // Generate new math problem
        const num1 = Math.floor(Math.random() * 10) + 1;
        const num2 = Math.floor(Math.random() * 10) + 1;
        setMathProblem({
          num1,
          num2,
          answer: num1 + num2
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      setSubmitMessage('There was a problem submitting your form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (formStarted && !formSubmitted) {
        window.gtag && window.gtag('event', 'contact_form_abandon', {
          event_category: 'engagement',
          event_label: 'Contact Page',
          value: 1
        });
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formStarted, formSubmitted]);

  const generateNewMathProblem = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setMathProblem({
      num1,
      num2,
      answer: num1 + num2
    });
    setFormData(prev => ({ ...prev, mathAnswer: '' }));
  };

  return (
    <div className="contact-section">
      <Helmet>
        <title>Contact Us - Szakacs Visuals</title>
        <meta name="description" content="Get in touch with Szakacs Visuals for professional photography and videography services in Bristol. Book your session or request a quote today." />
        <meta name="keywords" content="contact photographer, book photography session, wedding photography booking, Bristol photographer contact" />
        <meta property="og:title" content="Contact Us - Szakacs Visuals" />
        <meta property="og:description" content="Get in touch with Szakacs Visuals for professional photography and videography services in Bristol. Book your session or request a quote today." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://szakacsvisuals.co.uk/contact" />
        <meta property="og:image" content="https://szakacsvisuals.co.uk/og-cover.jpg" />
        <meta property="og:image:alt" content="Contact Szakacs Visuals" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <link rel="canonical" href="https://szakacsvisuals.co.uk/contact" />
      </Helmet>
      <h1>Let’s Bring Your Vision to Life</h1>
<p>Got a project or an idea? I’d love to hear from you. Fill out the form and I’ll get back to you within 24 hours.</p>

      {submitMessage && <div className="success-message">{submitMessage}</div>}

      <form
        className="contact-form"
        name="contact"
        method="POST"
        data-netlify="true"
        netlify-honeypot="bot-field"
        autoComplete="off"
        onSubmit={handleSubmit}
      >
        <input type="hidden" name="form-name" value="contact" />
        <p style={{ display: 'none' }}>
          <label>
            Don't fill this out: <input name="bot-field" />
          </label>
        </p>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="firstName">First Name *</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              aria-required="true"
            />
            {errors.firstName && <span className="error-message">{errors.firstName}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="lastName">Last Name *</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              aria-required="true"
            />
            {errors.lastName && <span className="error-message">{errors.lastName}</span>}
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              aria-required="true"
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="phone">Phone Nr. *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              aria-required="true"
            />
            {errors.phone && <span className="error-message">{errors.phone}</span>}
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="serviceDate">Preferred Date *</label>
          <input
            type="date"
            id="serviceDate"
            name="serviceDate"
            value={formData.serviceDate}
            onChange={handleChange}
            required
            aria-required="true"
            min={today}
            max={maxDateString}
          />
          {errors.serviceDate && <span className="error-message">{errors.serviceDate}</span>}
        </div>
        <div className="form-group">
          <label htmlFor="message">Message *</label>
          <textarea
            id="message"
            name="message"
            rows="5"
            value={formData.message}
            onChange={handleChange}
            required
            aria-required="true"
          />
          {errors.message && <span className="error-message">{errors.message}</span>}
        </div>
        <div className="form-group">
          <label htmlFor="mathAnswer">
            Please solve this simple math problem to verify you're human: {mathProblem.num1} + {mathProblem.num2} = ?
            <button type="button" onClick={generateNewMathProblem} aria-label="Refresh math problem" style={{marginLeft: '0.5em'}}>
              ↻
            </button>
          </label>
          <input
            type="number"
            id="mathAnswer"
            name="mathAnswer"
            value={formData.mathAnswer}
            onChange={handleChange}
            required
            aria-required="true"
            min="0"
            max="20"
          />
          {errors.mathAnswer && <span className="error-message">{errors.mathAnswer}</span>}
        </div>
        {errors.timing && <div className="error-message timing-error">{errors.timing}</div>}
        <button 
          type="submit" 
          className="submit-btn"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </form>
    </div>
  );
}

export default Contact; 